import Phaser from "phaser";
import {
  ModalLayer,
  NineSlicePanel,
  NineSliceButton,
  NINE_SLICE_BUTTON_EVENT,
} from "@yjsgames/phaser-ui";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, PRIMARY_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { getTextDirection } from "../i18n/i18n.js";
import { popupCloseButtonX } from "./directionalUi.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";

export interface ConfirmPopupOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly title: string;
  /** 본문(경고) — `\n`로 줄바꿈, 폭 초과 시 자동 줄바꿈. */
  readonly message: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  /** true면 확인 버튼 라벨을 위험색(signalDanger)으로 — 파괴적 액션 강조. */
  readonly danger?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

const PANEL_W = 280;
const PANEL_H = 176;
/** 확인/취소 버튼의 중심 x 오프셋(패널 중앙 기준 ∓). */
const BTN_DX = 62;

/** 확인/취소 버튼 중심 y — E2E 클릭 좌표 SSoT(레이아웃 변경 자동 추종). */
export const confirmPopupButtonCenterY = (screenHeight: number): number =>
  screenHeight / 2 + PANEL_H / 2 - 28;
/** 확인 버튼 중심 x(패널 중앙 - BTN_DX). */
export const confirmPopupConfirmX = (screenWidth: number): number => screenWidth / 2 - BTN_DX;
/** 취소 버튼 중심 x(패널 중앙 + BTN_DX). */
export const confirmPopupCancelX = (screenWidth: number): number => screenWidth / 2 + BTN_DX;

/**
 * 범용 yes/no 확인 팝업 — GridUnlockConfirmPopup 패턴(ModalLayer + NineSlicePanel). 파괴적/되돌릴 수
 * 없는 액션(계정 삭제 등) 직전에 "정말?" 한 번 더 묻는다. 확인/취소 콜백은 호출자 소유(본 팝업은 표시·발화만).
 * danger=true면 확인 라벨을 signalDanger 색으로 칠해 위험을 강조한다.
 */
export class ConfirmPopup extends ModalLayer {
  constructor(scene: Phaser.Scene, opts: ConfirmPopupOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.82,
      onBackdropClick: () => opts.onCancel(),
    });

    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const top = cy - PANEL_H / 2;
    this.setBackdropCloseExclusion(cx - PANEL_W / 2, top, PANEL_W, PANEL_H);

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: PANEL_W,
      height: PANEL_H,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);

    const title = scene.add
      .text(cx, top + 24, opts.title, {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textBright,
      })
      .setOrigin(0.5);
    const closeBtn = scene.add
      .text(popupCloseButtonX(cx, PANEL_W, 18, getTextDirection()), top + 16, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.display,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => opts.onCancel());
    const message = scene.add
      .text(cx, top + 78, opts.message, {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textPrimary,
        align: "center",
        wordWrap: { width: PANEL_W - 36 },
      })
      .setOrigin(0.5);
    this.contentContainer.add([title, closeBtn, message]);

    // 확인 — danger면 라벨 위험색. 취소와 좌우 2버튼.
    const confirmStyle = opts.danger
      ? { ...PRIMARY_BUTTON_STYLE, textStyle: { ...PRIMARY_BUTTON_STYLE.textStyle, color: PALETTE.signalDanger } }
      : PRIMARY_BUTTON_STYLE;
    const confirmBtn = new NineSliceButton(scene, cx - BTN_DX, top + PANEL_H - 28, {
      style: confirmStyle,
      label: opts.confirmLabel,
      widthOverride: 108,
    });
    confirmBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onConfirm());
    const cancelBtn = new NineSliceButton(scene, cx + BTN_DX, top + PANEL_H - 28, {
      style: PRIMARY_BUTTON_STYLE,
      label: opts.cancelLabel,
      widthOverride: 108,
    });
    cancelBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onCancel());
    this.contentContainer.add([confirmBtn, cancelBtn]);
  }
}
